// Sidebar component

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  MapPin,
  Building2,
  Filter,
  BarChart3,
  X,
} from 'lucide-react';
import { PROVINCES, PROXIMITY_RADII, FAST_FOOD_BRANDS } from '../../utils/constants';
import { formatNumber } from '../../utils/formatters';
import Badge from '../ui/Badge';

const Sidebar = ({
  isOpen,
  onClose,
  pods = [],
  selectedProvince,
  onProvinceChange,
  selectedRadius,
  onRadiusChange,
  selectedBrands,
  onBrandToggle,
  stats,
  className = '',
}) => {
  const [expandedSection, setExpandedSection] = useState('provinces');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Group PODS by province
  const podsByProvince = pods.reduce((acc, pod) => {
    if (!acc[pod.province]) {
      acc[pod.province] = [];
    }
    acc[pod.province].push(pod);
    return acc;
  }, {});

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-80 bg-white border-r border-dark-200
          transform transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
          ${className}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-200 lg:hidden">
          <h2 className="text-lg font-semibold text-dark-900">Filters</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-dark-600 hover:bg-dark-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Stats Overview */}
          {stats && (
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-4 text-white">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-5 h-5" />
                <span className="font-medium">Overview</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-2xl font-bold">{formatNumber(stats.total)}</div>
                  <div className="text-sm text-white/80">Total PODS</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{Object.keys(stats.byProvince || {}).length}</div>
                  <div className="text-sm text-white/80">Provinces</div>
                </div>
              </div>
            </div>
          )}

          {/* Provinces Section */}
          <div className="border border-dark-200 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection('provinces')}
              className="w-full flex items-center justify-between p-3 bg-dark-50 hover:bg-dark-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary-500" />
                <span className="font-medium text-dark-900">Provinces</span>
              </div>
              {expandedSection === 'provinces' ? (
                <ChevronDown className="w-4 h-4 text-dark-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-dark-500" />
              )}
            </button>

            {expandedSection === 'provinces' && (
              <div className="p-3 space-y-1">
                <button
                  onClick={() => onProvinceChange?.(null)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 rounded-lg
                    transition-colors
                    ${!selectedProvince
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-dark-700 hover:bg-dark-50'}
                  `}
                >
                  <span>All Provinces</span>
                  <Badge variant="primary" size="small">
                    {pods.length}
                  </Badge>
                </button>

                {Object.entries(PROVINCES).map(([key, province]) => {
                  const count = podsByProvince[key]?.length || 0;
                  if (count === 0) return null;

                  return (
                    <button
                      key={key}
                      onClick={() => onProvinceChange?.(key)}
                      className={`
                        w-full flex items-center justify-between px-3 py-2 rounded-lg
                        transition-colors
                        ${selectedProvince === key
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-dark-700 hover:bg-dark-50'}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: province.color }}
                        />
                        <span>{province.name}</span>
                      </div>
                      <Badge variant="default" size="small">
                        {count}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Radius Filter */}
          <div className="border border-dark-200 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection('radius')}
              className="w-full flex items-center justify-between p-3 bg-dark-50 hover:bg-dark-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-secondary-500" />
                <span className="font-medium text-dark-900">Proximity Radius</span>
              </div>
              {expandedSection === 'radius' ? (
                <ChevronDown className="w-4 h-4 text-dark-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-dark-500" />
              )}
            </button>

            {expandedSection === 'radius' && (
              <div className="p-3 space-y-2">
                {PROXIMITY_RADII.map((radius) => (
                  <button
                    key={radius.value}
                    onClick={() => onRadiusChange?.(radius.value)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2 rounded-lg
                      transition-colors
                      ${selectedRadius === radius.value
                        ? 'bg-secondary-50 text-secondary-700'
                        : 'text-dark-700 hover:bg-dark-50'}
                    `}
                  >
                    <span className="font-medium">{radius.label}</span>
                    <span className="text-xs text-dark-500">{radius.description}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Fast Food Brands */}
          <div className="border border-dark-200 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection('brands')}
              className="w-full flex items-center justify-between p-3 bg-dark-50 hover:bg-dark-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-accent-500" />
                <span className="font-medium text-dark-900">Fast Food Brands</span>
              </div>
              {expandedSection === 'brands' ? (
                <ChevronDown className="w-4 h-4 text-dark-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-dark-500" />
              )}
            </button>

            {expandedSection === 'brands' && (
              <div className="p-3 space-y-1 max-h-64 overflow-y-auto">
                {FAST_FOOD_BRANDS.map((brand) => {
                  const isSelected = selectedBrands?.includes(brand.name);

                  return (
                    <button
                      key={brand.name}
                      onClick={() => onBrandToggle?.(brand.name)}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 rounded-lg
                        transition-colors
                        ${isSelected
                          ? 'bg-accent-50 text-accent-700'
                          : 'text-dark-700 hover:bg-dark-50'}
                      `}
                    >
                      <span className="text-lg">{brand.logo}</span>
                      <span className="flex-1 text-left">{brand.name}</span>
                      <span
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center
                          ${isSelected
                            ? 'bg-accent-500 border-accent-500'
                            : 'border-dark-300'}
                        `}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-dark-200">
          <p className="text-xs text-dark-500 text-center">
            FRONTSEAT Dashboard v1.0.0
          </p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
